// src/components/auth/RequirePermission.jsx
// ------------------------------------------------------------------
//
// Works with AuthContext (hasPermission, initialized) and
// OrganizationContext2 (isCurrentUserOwner).  Supports:
//
//   • AND logic     <RequirePermission permissions="manageUsers">…</…>
//   • OR logic      <RequirePermission permissions={["edit","publish"]} any>
//   • Ownership     <RequirePermission permissions="manageBilling" requireOwnership>
//   • Deny modes
//        redirect   (default)  →  Navigate to /403
//        hide       →  render nothing
//        placeholder={<Alert …/>}  →  show a custom element
//
// ------------------------------------------------------------------

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useOrganization2 } from "../../contexts/OrganizationContext2";

/**
 * @param {string|string[]} permissions        – one permission OR array
 * @param {boolean}         any               – OR logic (default = AND)
 * @param {boolean}         requireOwnership  – also require org ownership
 *
 * @param {"redirect"|"hide"} deny            – how to respond if NOT authorised
 *        • "redirect"  → Navigate to /403 (default)
 *        • "hide"      → render null
 *
 * @param {ReactNode}      placeholder        – override for deny output
 *                                             (ignored if deny === "hide")
 * @param {string}         fallback           – path for redirect (default "/403")
 * @param {ReactNode}      children           – protected content
 */
export default function RequirePermission({
  permissions,
  any = false,
  requireOwnership = false,
  deny = "redirect",
  placeholder = null,
  fallback = "/403",
  children,
}) {
  const { initialized, hasPermission } = useAuth();
  const { isCurrentUserOwner } = useOrganization2();
  const location = useLocation();

  /* 0.  Wait for Auth bootstrap ---------------------------------- */
  if (!initialized) return null; // or a tiny spinner if you prefer

  /* 1.  Evaluate permission -------------------------------------- */
  const requested = Array.isArray(permissions) ? permissions : [permissions];

  // AND vs OR semantics
  const passByPerm = any
    ? requested.some((perm) => hasPermission(perm))
    : hasPermission(requested);

  /* 2.  Ownership (optional) ------------------------------------- */
  const passByOwner =
    !requireOwnership ? true : isCurrentUserOwner?.() ?? false;

  /* 3.  Final verdict -------------------------------------------- */
  const authorised = passByPerm && passByOwner;

  if (authorised) return children;

  /* -------- Not authorised: choose deny strategy ---------------- */
  if (placeholder !== null && deny !== "hide") return <>{placeholder}</>;

  if (deny === "hide") return null;

  // Default → redirect
  return (
    <Navigate
      to={fallback}
      replace
      state={{ from: location }}
    />
  );
}

/* -----------------------------------------------------------------
 * Usage Patterns
 * -----------------------------------------------------------------
 *
 * 1) Full-page lockout (redirect to 403)
 *    <RequirePermission permissions="manageUsers">
 *        <UserManagementPage/>
 *    </RequirePermission>
 *
 * 2) Hide a sidebar button entirely
 *    {hasPermission("manageUsers") && (
 *        <Button component={Link} to="/settings/user-management">Users</Button>
 *    )}
 *
 * 3) Allow read-only view, disable editing controls
 *    const canEdit = hasPermission("editForms");
 *    <RequirePermission permissions="editForms" deny="hide">
 *        <EditableSection/>
 *    </RequirePermission>
 *    {!canEdit && <ReadOnlySection/>}
 *
 * 4) Same as #1 but show an inline alert instead of redirect
 *    <RequirePermission
 *        permissions="manageBilling"
 *        requireOwnership
 *        deny="placeholder"
 *        placeholder={<Alert severity="error">Owner access only</Alert>}
 *    >
 *        <BillingSettingsPage/>
 *    </RequirePermission>
 *
 * -----------------------------------------------------------------*/
